import {
  type SharePrepareRequest,
  type ShopDto,
  type TournamentDto,
  type UpdateProfileRequest,
  type ZoneDto,
  inviteFromStartParam,
  isPlayStartParam,
} from '@penalty-cup/shared';
import { create } from 'zustand';
import { ApiError, api } from './api.js';
import {
  type GameData,
  initialGame,
  myRole,
  onMatchFinished,
  onMatchState,
  onRoundResult,
  onRoundStart,
  onTournament,
} from './game/session.js';
import { haptic } from './haptics.js';
import { TOURNAMENT_ERRORS } from './strings.js';
import { openShareDialog } from './share.js';
import { play as sfx, setSoundEnabled, soundEnabled } from './sound.js';
import { type GameSocket, connectGame } from './socket.js';

export type { Session } from './game/session.js';

/** Сколько показываем решающий удар до экрана итога (сервер ждёт NEXT_MATCH_DELAY_MS = 6 с) */
export const FINAL_KICK_REVEAL_MS = 2_000;
let lastKickAt = 0;

interface Actions {
  signIn: (initData: string | null) => Promise<void>;
  play: (size?: 4 | 8) => Promise<void>;
  buyTicket: () => Promise<void>;
  closeTicketModal: () => void;
  openTicketModal: (size: 4 | 8) => void;
  playQuick: () => Promise<void>;
  setCoins: (coins: number) => void;
  dismissResult: () => void;
  chooseZone: (zone: ZoneDto) => void;
  setPower: (power: number) => void;
  setAccuracy: (accuracy: number) => void;
  /** Сохраняет имя команды и форму; после онбординга — на тренировку. */
  saveProfile: (patch: UpdateProfileRequest, startTraining?: boolean) => Promise<void>;
  setOnboardingStep: (step: GameData['onboardingStep']) => void;
  setView: (view: GameData['view']) => void;
  share: (req: SharePrepareRequest) => Promise<void>;
  toggleSound: () => void;
  createDuel: () => Promise<void>;
  createFriendsCup: (size: 4 | 8) => Promise<void>;
  acceptInvite: (code: string) => Promise<void>;
  startFriendly: () => Promise<void>;
  leaveTournament: () => Promise<void>;
  goHome: () => void;
  answerIncoming: (accept: boolean) => Promise<void>;
  /** обновить монеты и надетое после покупки/экипировки */
  applyShop: (shop: ShopDto) => void;
  reloadMe: () => Promise<void>;
}

let socket: GameSocket | null = null;
let actionSeq = 0;
const newActionId = () => `${Date.now().toString(36)}-${(++actionSeq).toString(36)}`;
/** Задержка, после которой клиент сам говорит «готов»: столько показывается интро матча. */
const INTRO_MS = 1500;

export const useApp = create<GameData & Actions>((set, get) => {
  function bindSocket(token: string) {
    socket?.disconnect();
    const s = connectGame(token);
    socket = s;
    s.on('connect', () => set({ reconnecting: false }));
    s.on('disconnect', () => set({ reconnecting: true }));
    s.on('connect_error', () => set({ reconnecting: true }));
    s.on('invite:incoming', (invite) => {
      haptic('goal');
      set({ incomingInvite: invite });
    });
    s.on('invite:declined', ({ name }) => set({ notice: `${name} пока не может — вызов отклонён` }));
    s.on('tournament:update', (t) => {
      const before = get().tournament;
      set(onTournament(get(), t));
      const me = t.players.find((p) => p.isYou);
      if (t.status === 'FINISHED' && before?.status !== 'FINISHED' && me && t.championId === me.id)
        haptic('win');
    });
    s.on('match:state', (m) => {
      set(onMatchState(get(), m));
      if (m.phase === 'READY') setTimeout(() => s.emit('match:ready', { matchId: m.matchId }), INTRO_MS);
    });
    s.on('match:start', () => sfx('whistle'));
    s.on('round:start', (t) => set(onRoundStart(get(), t)));
    s.on('action:accepted', (a) => {
      if (get().turn?.roundId === a.roundId) set({ stage: 'sent' });
    });
    s.on('round:result', (r) => {
      lastKickAt = Date.now();
      const role = myRole(get());
      set(onRoundResult(get(), r));
      const k = r.kick;
      if (k.outcome === 'GOAL') haptic(role === 'shooter' && k.quality === 'PERFECT' ? 'perfect' : 'goal');
      else if (k.outcome === 'SAVE') haptic('save');
      // Удар — сразу, исход — когда мяч долетит (анимация 0,5 с)
      sfx('kick');
      setTimeout(
        () =>
          sfx(
            k.outcome === 'GOAL'
              ? 'goal'
              : k.outcome === 'SAVE'
                ? 'save'
                : k.outcome === 'POST'
                  ? 'post'
                  : 'miss',
          ),
        450,
      );
    });
    s.on('score:update', () => undefined);
    s.on('match:finished', (r) => {
      // Решающий удар только что прилетел — даём досмотреть гол или сейв, потом экран итога
      const left = FINAL_KICK_REVEAL_MS - (Date.now() - lastKickAt);
      if (left > 0 && get().match?.matchId === r.matchId) {
        set({ ...onMatchFinished(get(), r), finalReveal: true });
        setTimeout(() => {
          if (get().matchResult?.matchId === r.matchId) set({ finalReveal: false });
        }, left);
      } else {
        set({ ...onMatchFinished(get(), r), finalReveal: false });
      }
      // Рейтинг и статистика изменились — обновляем профиль
      const { token: t } = get();
      if (t)
        void api.me(t).then(
          (me) => set({ me }),
          () => undefined,
        );
      const won = r.winner === get().match?.you;
      haptic(won ? 'win' : 'lose');
      setTimeout(() => sfx(won ? 'victory' : 'defeat'), 900);
    });
    s.on('game:error', (e) => {
      // Ход не принят (опоздал, удар уже разрешён) — ждём следующего удара; остальное — в консоль
      if (e.code !== 'stale_round') console.warn('game:error', e);
    });
  }

  /** Действие, возвращающее турнир (создать дуэль/кубок, принять приглашение, начать) — с понятной ошибкой. */
  /** Возвращает код ошибки сервера (null — успех). Текущий матч не трогаем, пока сервер не ответил успехом. */
  async function tournamentAction(fn: (token: string) => Promise<TournamentDto>): Promise<string | null> {
    const { token, joining } = get();
    if (!token || joining) return 'busy';
    haptic('tap');
    set({ joining: true, notice: null });
    try {
      const t = await fn(token);
      set({ ...onTournament(get(), t), matchResult: null, match: null, lastKick: null, view: null });
      return null;
    } catch (err) {
      const code = err instanceof ApiError ? err.message : '';
      set({ notice: TOURNAMENT_ERRORS[code] ?? 'Не получилось. Проверьте связь и попробуйте ещё раз' });
      return code || 'network';
    } finally {
      set({ joining: false });
    }
  }

  return {
    ...initialGame,
    soundOn: soundEnabled(),

    signIn: async (initData) => {
      if (!initData) {
        set({ session: 'outside' });
        return;
      }
      set({ session: 'starting' });
      try {
        const res = await api.loginWithTelegram(initData);
        set({ session: 'ready', token: res.accessToken, me: res.me });
        bindSocket(res.accessToken);
        // Цены, награды и лимиты экономики (меняются в админке без выкладки)
        void api.economySettings(res.accessToken).then(
          (settings) => set({ settings }),
          () => undefined,
        );
        const current = await api.currentTournament(res.accessToken).catch(() => null);
        if (current?.tournament && !get().tournament) set(onTournament(get(), current.tournament));
        // Открыли по ссылке «играть» — сразу в турнир (новичок — после онбординга)
        if (isPlayStartParam(res.startParam) && !get().tournament) {
          if (res.me.teamName) void get().play();
          else set({ autoPlay: true });
        }
        // Сообщение бота «слот заканчивается» — сразу окно продления
        if (res.startParam === 'slots') set({ slotOfferOpen: true });
        // Открыли по ссылке-приглашению друга — в его дуэль или кубок (новичок — после онбординга)
        const invite = inviteFromStartParam(res.startParam);
        if (invite) {
          if (res.me.teamName) void get().acceptInvite(invite.code);
          else set({ pendingInvite: invite.code });
        }
      } catch (err) {
        set({ session: err instanceof ApiError && err.status === 401 ? 'denied' : 'offline' });
      }
    },

    play: async (size = 8) => {
      const { token, joining, me } = get();
      if (!token || joining) return;
      haptic('tap');
      // Билетов нет — сразу окно покупки (сервер всё равно проверит)
      if (me && me.tickets.total === 0) {
        set({ ticketModal: { size, bought: false } });
        return;
      }
      set({
        joining: true,
        matchResult: null,
        match: null,
        lastKick: null,
        view: null,
        notice: null,
        ticketModal: null,
      });
      try {
        const t = await api.joinTournament(token, size).catch((err: unknown) => {
          if (err instanceof ApiError && err.message === 'no_tickets') {
            set({ ticketModal: { size, bought: false } });
            return null;
          }
          set({
            notice:
              err instanceof ApiError && err.status === 429
                ? 'Слишком часто. Подождите минуту и попробуйте снова'
                : 'Не удалось войти в турнир. Проверьте связь и попробуйте ещё раз',
          });
          return null;
        });
        if (!t) return;
        set(onTournament(get(), t));
        const me = await api.me(token).catch(() => null);
        if (me) set({ me });
      } finally {
        set({ joining: false });
      }
    },

    saveProfile: async (patch, startTraining = false) => {
      const { token } = get();
      if (!token) return;
      const me = await api.updateProfile(token, patch);
      set({ me, ...(startTraining ? { onboardingStep: 'trainShot' as const } : {}) });
    },

    setOnboardingStep: (onboardingStep) => {
      set({ onboardingStep });
      if (onboardingStep === null && get().pendingInvite) {
        set({ autoPlay: false });
        void get().acceptInvite(get().pendingInvite!);
      } else if (onboardingStep === null && get().autoPlay) {
        set({ autoPlay: false });
        void get().play();
      }
    },

    toggleSound: () => {
      const on = !get().soundOn;
      setSoundEnabled(on);
      set({ soundOn: on });
      if (on) sfx('whistle');
    },

    createDuel: async () => {
      await tournamentAction((token) => api.createDuel(token));
    },
    createFriendsCup: async (size) => {
      await tournamentAction((token) => api.createFriendsCup(token, size));
    },
    acceptInvite: async (code) => {
      const error = await tournamentAction((token) => api.joinInvite(token, code));
      // Идёт турнир — приглашение не теряем: кнопка «Принять вызов друга», когда игрок освободится
      if (error === 'already_in_tournament') {
        set({
          pendingInvite: code,
          notice: 'Вызов друга сохранён — примем его, когда доиграете этот турнир',
        });
      } else if (error !== 'busy') {
        set({ pendingInvite: null });
      }
    },
    startFriendly: async () => {
      const t = get().tournament;
      if (t) await tournamentAction((token) => api.startTournament(token, t.id));
    },
    leaveTournament: async () => {
      const { token, tournament } = get();
      if (!token || !tournament) return;
      haptic('tap');
      await api.leaveTournament(token, tournament.id).catch(() => undefined);
      set({ tournament: null, notice: null });
      // Вышел из поиска — билет вернулся
      await get().reloadMe();
    },
    buyTicket: async () => {
      const { token, me, ticketModal } = get();
      if (!token || !me) return;
      haptic('tap');
      try {
        const r = await api.buyTicket(token);
        set({
          me: { ...me, coins: r.coins, tickets: r.tickets },
          ticketModal: ticketModal && { ...ticketModal, bought: true },
        });
      } catch (err) {
        set({
          notice:
            err instanceof ApiError && err.message === 'not_enough_coins'
              ? 'Не хватает серебра'
              : 'Не получилось купить билет',
        });
        await get().reloadMe();
      }
    },
    closeTicketModal: () => set({ ticketModal: null }),
    openTicketModal: (size) => set({ ticketModal: { size, bought: false } }),
    setCoins: (coins) => {
      const me = get().me;
      if (me && me.coins !== coins) set({ me: { ...me, coins } });
    },
    playQuick: async () => {
      await tournamentAction((token) => api.quickMatch(token));
    },
    applyShop: (shop) => {
      const me = get().me;
      if (me) set({ me: { ...me, coins: shop.coins, equipped: shop.equipped } });
    },
    reloadMe: async () => {
      const token = get().token;
      if (!token) return;
      const me = await api.me(token).catch(() => null);
      if (me) set({ me });
    },
    answerIncoming: async (accept) => {
      const { incomingInvite: invite, token } = get();
      if (!invite) return;
      set({ incomingInvite: null });
      if (accept) await get().acceptInvite(invite.code);
      else if (token) await api.declineInvite(token, invite.code).catch(() => undefined);
    },
    goHome: () => {
      haptic('tap');
      set({ tournament: null, match: null, matchResult: null, lastKick: null, view: null });
    },

    share: async (req) => {
      const { token, sharing } = get();
      if (!token || sharing === 'busy') return;
      haptic('tap');
      set({ sharing: 'busy' });
      try {
        await openShareDialog(await api.prepareShare(token, req));
        set({ sharing: 'idle' });
      } catch {
        set({ sharing: 'failed' });
      }
    },

    setView: (view) => {
      haptic('tap');
      set({ view });
    },

    dismissResult: () => {
      haptic('tap');
      set({ matchResult: null, match: null, lastKick: null });
    },

    chooseZone: (zone) => {
      const s = get();
      const role = myRole(s);
      if (!s.turn || !s.match || s.stage !== 'zone' || !role) return;
      haptic('tap');
      if (role === 'keeper') {
        set({ stage: 'sent', draft: { zone } });
        socket?.emit('keeper:submit', {
          matchId: s.match.matchId,
          roundId: s.turn.roundId,
          actionId: newActionId(),
          zone,
        });
      } else {
        set({ stage: 'power', draft: { zone } });
      }
    },

    setPower: (power) => {
      const s = get();
      if (s.stage !== 'power') return;
      haptic('tap');
      set({ stage: 'accuracy', draft: { ...s.draft, power } });
    },

    setAccuracy: (accuracy) => {
      const s = get();
      if (s.stage !== 'accuracy' || !s.turn || !s.match || !s.draft.zone || s.draft.power === undefined)
        return;
      haptic('tap');
      set({ stage: 'sent' });
      socket?.emit('shot:submit', {
        matchId: s.match.matchId,
        roundId: s.turn.roundId,
        actionId: newActionId(),
        zone: s.draft.zone,
        power: s.draft.power,
        accuracy,
      });
    },
  };
});
